import React from "react";
import {
  Search,
  MapPin,
  MessageCircle,
  Users,
  Camera,
  Bell,
  ShieldCheck,
  Heart,
  Sparkles,
  PawPrint,
  Mail,
  Smartphone,
  CheckCircle2,
} from "lucide-react";
import { APPLE_STORE_URL, GOOGLE_PLAY_URL, CONTACT_EMAIL } from "../lib/constants";
import DownloadStores from "../components/DownloadStores";

const FeatureGroup = ({
  icon: Icon,
  title,
  items,
  accent = "purple",
  testId,
}) => (
  <div
    data-testid={testId}
    className="relative rounded-3xl bg-white border border-[#7c3aed]/12 p-7 sm:p-8 shadow-[0_10px_30px_-15px_rgba(124,58,237,0.25)]"
  >
    <div className="flex items-center gap-4 mb-5">
      <div
        className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white ${
          accent === "teal" ? "icon-bubble-teal" : "icon-bubble"
        }`}
      >
        <Icon size={26} strokeWidth={2.2} />
      </div>
      <h3 className="font-display font-bold text-xl text-[#2e1065]">
        {title}
      </h3>
    </div>
    <ul className="space-y-2.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3 text-sm text-[#3c1e6b]/80">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#14B8A6]" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Differential = ({ text, testId }) => (
  <div
    data-testid={testId}
    className="flex items-start gap-3 rounded-2xl bg-white border border-[#7c3aed]/12 px-5 py-4 shadow-[0_8px_20px_-15px_rgba(124,58,237,0.25)]"
  >
    <div className="shrink-0 w-8 h-8 rounded-xl bg-[#7c3aed]/10 flex items-center justify-center">
      <PawPrint size={16} className="text-[#7c3aed]" />
    </div>
    <p className="text-sm text-[#3c1e6b]/85 leading-relaxed font-medium">
      {text}
    </p>
  </div>
);

export default function About() {
  return (
    <div data-testid="about-page">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden brand-bg">
        <div className="absolute inset-0 paw-pattern opacity-60 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-12 sm:pb-16 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 border border-[#7c3aed]/15 text-xs sm:text-sm font-semibold text-[#6d28d9] mb-6 shadow-sm">
            <Heart size={14} className="text-[#14B8A6] fill-[#14B8A6]" />
            Sobre o Pets · Perdidos e Achados
          </span>

          <h1
            className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2e1065] fade-up"
            data-testid="about-title"
          >
            Feito para <span className="brand-gradient-text">reaproximar</span><br />
            pessoas e animais.
          </h1>
          <p
            className="mt-6 text-base sm:text-lg text-[#3c1e6b]/75 leading-relaxed max-w-2xl mx-auto fade-up fade-up-1"
            data-testid="about-subtitle"
          >
            Um aplicativo brasileiro feito com o propósito de ajudar pessoas
            e animais a se reencontrarem — porque a gente sabe o amor que
            existe entre vocês.
          </p>
        </div>
      </section>

      {/* ===== MISSION / WHAT IS IT ===== */}
      <section className="relative py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="rounded-[2rem] glass-card p-8 sm:p-12 shadow-[0_20px_50px_-25px_rgba(124,58,237,0.35)]">
            <div className="grid sm:grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start">
              <div className="w-16 h-16 rounded-2xl icon-bubble flex items-center justify-center text-white">
                <Sparkles size={28} strokeWidth={2.2} />
              </div>
              <div>
                <h2 className="font-display font-bold text-2xl sm:text-3xl text-[#2e1065] leading-tight">
                  O que é o Pets · Perdidos e Achados
                </h2>
                <p className="mt-4 text-[#3c1e6b]/80 leading-relaxed">
                  Um aplicativo criado para conectar pessoas que perderam seus
                  animais com quem os encontrou, tornando a busca muito mais
                  rápida, organizada e eficiente. Tutores desesperados procurando
                  seus pets e pessoas que encontraram um animal na rua agora têm
                  um lugar único para se ajudarem.
                </p>
                <p className="mt-4 text-[#3c1e6b]/80 leading-relaxed">
                  Tudo em uma interface moderna, simples e intuitiva, pensada
                  para funcionar bem até para quem não tem familiaridade com
                  tecnologia.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== MAIN FEATURES (detailed) ===== */}
      <section className="relative py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#14B8A6]">
              <PawPrint size={14} /> Funcionalidades principais
            </span>
            <h2
              className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#2e1065] mt-3 leading-[1.1]"
              data-testid="features-detailed-title"
            >
              Tudo organizado para <span className="brand-gradient-text">agilizar reencontros.</span>
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <FeatureGroup
              testId="feature-group-search"
              icon={Search}
              title="Procurar Pet"
              items={[
                "Veja fotos dos animais perdidos e encontrados",
                "Confira descrição e características detalhadas",
                "Identifique raça, porte e cor",
                "Acompanhe a última localização registrada",
                "Filtre por região e encontre ocorrências recentes",
              ]}
            />
            <FeatureGroup
              testId="feature-group-publish"
              icon={Camera}
              accent="teal"
              title="Perdi ou Encontrei um Pet"
              items={[
                "Cadastre rapidamente com foto e nome do pet",
                "Informe data do desaparecimento e região",
                "Descreva características específicas e contato",
                "Defina a situação: perdido, encontrado ou avistado",
                "Anúncio completo em poucos segundos",
              ]}
            />
            <FeatureGroup
              testId="feature-group-map"
              icon={MapPin}
              title="Mapa de Ocorrências"
              items={[
                "Registre no mapa o local exato do desaparecimento",
                "Marque onde encontrou um pet",
                "Visualize ocorrências próximas de você",
                "Acompanhe movimentações em tempo real",
                "Crie pontos de avistamento colaborativos",
              ]}
            />
            <FeatureGroup
              testId="feature-group-chat"
              icon={MessageCircle}
              accent="teal"
              title="Sistema de Mensagens"
              items={[
                "Converse diretamente com outros usuários",
                "Envie novas pistas e avistamentos",
                "Combine resgates com segurança",
                "Confirme detalhes e troque fotos",
                "Tudo dentro do próprio aplicativo",
              ]}
            />
          </div>

          {/* Community card full-width */}
          <div className="mt-6 rounded-3xl p-8 sm:p-10 border border-[#7c3aed]/12 bg-gradient-to-br from-white via-[#f5edff] to-white shadow-[0_10px_30px_-15px_rgba(124,58,237,0.25)]">
            <div className="grid sm:grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start">
              <div className="w-14 h-14 rounded-2xl icon-bubble flex items-center justify-center text-white shrink-0">
                <Users size={26} strokeWidth={2.2} />
              </div>
              <div>
                <h3 className="font-display font-bold text-xl sm:text-2xl text-[#2e1065]">
                  Comunidade colaborativa
                </h3>
                <p className="mt-3 text-[#3c1e6b]/80 leading-relaxed">
                  O aplicativo não serve apenas para quem perdeu um pet. Ele
                  incentiva pessoas da comunidade a ajudarem informando
                  avistamentos, compartilhando publicações, registrando
                  animais encontrados e auxiliando nos resgates. Quanto mais
                  pessoas usando, maior a chance de reencontros felizes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== OBJECTIVE ===== */}
      <section className="relative py-16 sm:py-20 bg-gradient-to-b from-[#f5edff] to-[#faf7ff] border-y border-[#7c3aed]/8">
        <div className="absolute inset-0 paw-pattern opacity-50 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#14B8A6]">
            <Heart size={14} /> Nosso objetivo
          </span>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#2e1065] mt-3 leading-[1.1]">
            Reduzir o tempo de busca, <br />
            <span className="brand-gradient-text">multiplicar reencontros.</span>
          </h2>
          <p className="mt-6 text-[#3c1e6b]/80 leading-relaxed max-w-3xl mx-auto">
            Muitos pets desaparecem diariamente, e a dificuldade em divulgar
            rapidamente as informações torna os resgates mais lentos. O Pets
            Perdidos & Achados centraliza tudo num único lugar: anúncios,
            mapa, mensagens, localização e comunidade — criando uma
            experiência prática, rápida e emocionalmente importante para
            milhares de famílias.
          </p>
        </div>
      </section>

      {/* ===== DIFFERENTIALS ===== */}
      <section className="relative py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#14B8A6]">
              <ShieldCheck size={14} /> Diferenciais
            </span>
            <h2
              className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#2e1065] mt-3 leading-[1.1]"
              data-testid="differentials-title"
            >
              Por que o app é <span className="brand-gradient-text">único.</span>
            </h2>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Differential testId="diff-1" text="Interface moderna e intuitiva" />
            <Differential testId="diff-2" text="Localização em mapa em tempo real" />
            <Differential testId="diff-3" text="Sistema colaborativo de comunidade" />
            <Differential testId="diff-4" text="Foco exclusivo em pets perdidos e encontrados" />
            <Differential testId="diff-5" text="Comunicação direta entre usuários" />
            <Differential testId="diff-6" text="Publicações rápidas em poucos segundos" />
            <Differential testId="diff-7" text="Busca inteligente por região" />
            <Differential testId="diff-8" text="Visual moderno inspirado em apps atuais" />
            <Differential testId="diff-9" text="Simples mesmo para quem não tem familiaridade com tecnologia" />
          </div>
        </div>
      </section>

      {/* ===== CONTACT + CTA ===== */}
      <section className="relative py-16 sm:py-24" data-testid="about-contact-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="relative overflow-hidden rounded-[2rem] sm:rounded-[2.5rem] p-10 sm:p-16" style={{
            background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 50%, #14b8a6 140%)",
          }}>
            <div className="absolute inset-0 paw-pattern opacity-30 pointer-events-none mix-blend-overlay" />
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/10 rounded-full blur-3xl" />

            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-xs font-semibold text-white">
                  <Mail size={14} /> Fale com a gente
                </span>
                <h2 className="mt-5 font-display font-bold text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">
                  Tem uma história, dúvida ou sugestão?
                </h2>
                <p className="mt-4 text-white/85 text-base sm:text-lg leading-relaxed">
                  Adoramos receber histórias de reencontro e ouvir como
                  podemos melhorar. Mande um e-mail e a gente responde.
                </p>

                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="contact-email-btn"
                  className="mt-6 inline-flex items-center gap-3 bg-white text-[#6d28d9] font-semibold px-6 py-3.5 rounded-full hover:bg-[#f3e8ff] transition-all hover:-translate-y-0.5 shadow-xl break-all"
                >
                  <Mail size={18} />
                  {CONTACT_EMAIL}
                </a>
              </div>

              <div className="relative">
                <div className="rounded-3xl bg-white/10 backdrop-blur-md border border-white/25 p-8 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
                      <Smartphone size={22} />
                    </div>
                    <h3 className="font-display font-bold text-xl">
                      Baixe agora
                    </h3>
                  </div>
                  <p className="text-white/85 text-sm leading-relaxed mb-6">
                    Disponível para Android e iPhone. Faça parte da maior rede
                    colaborativa de localização de pets do Brasil.
                  </p>
                  <DownloadStores
                    variant="onDark"
                    compact
                    testId="about-download-stores"
                    className="!p-0 !bg-transparent !border-0 !shadow-none"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
